import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { AdminSubscribers } from "@/components/AdminSubscribers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "new", "contacted", "confirmed"];

const badgeClass = (s) =>
  s === "confirmed"
    ? "border-emerald-700/60 text-emerald-300"
    : s === "contacted"
      ? "border-amber-500/40 text-amber-300"
      : "border-sky-800/70 text-sky-300";

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("inquiries");
  const navigate = useNavigate();
  const token = localStorage.getItem("ono_admin_token");

  const load = useCallback(async () => {
    if (!token) return navigate("/admin");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filter === "all" ? {} : { status: filter },
      });
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ono_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate, token]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/inquiries/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setInquiries((list) => list.map((i) => (i.id === id ? data : i)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem("ono_admin_token");
    navigate("/admin");
  }, [navigate]);

  return (
    <main className="pt-32 pb-24 min-h-screen">
      <Seo
        title="Booking Inquiries — 1001 Nights Orchestra Admin"
        siteName="1001 Nights Orchestra"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-amber-400">
              Admin
            </p>
            <h1 className="mt-4 font-serif text-4xl text-amber-50 tracking-tight">
              {tab === "inquiries" ? "Booking Inquiries" : "Mailing List"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-amber-500/25 text-amber-200 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-amber-400/60 transition-colors"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-red-900/60 text-red-300/90 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-red-700 transition-colors"
            >
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>

        <div className="flex gap-6 border-b border-amber-500/15 mb-8">
          {[
            ["inquiries", "Inquiries"],
            ["subscribers", "Subscribers"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`pb-3 -mb-px font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
                tab === key
                  ? "border-gold text-amber-200"
                  : "border-transparent text-slate-500 hover:text-amber-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "subscribers" ? (
          <AdminSubscribers token={token} onUnauthorized={logout} />
        ) : (
          <>
            <div className="flex flex-wrap gap-3 mb-8">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`inquiry-filter-${s}`}
                  onClick={() => setFilter(s)}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-all ${
                    filter === s
                      ? "border-gold bg-gold/10 text-amber-200"
                      : "border-amber-500/15 text-slate-400 hover:border-amber-400/40"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            {loading ? (
              <p
                data-testid="inquiries-loading"
                className="text-slate-500 py-16 text-center font-serif text-2xl"
              >
                Loading inquiries...
              </p>
            ) : inquiries.length === 0 ? (
              <p
                data-testid="inquiries-empty"
                className="text-slate-500 py-16 text-center font-serif text-2xl"
              >
                No inquiries yet — the night is young.
              </p>
            ) : (
              <div data-testid="inquiries-list" className="space-y-4">
                {inquiries.map((q) => (
                  <article
                    key={q.id}
                    data-testid={`inquiry-row-${q.id}`}
                    className="border border-amber-500/15 bg-surface/60 p-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="font-serif text-2xl text-amber-100">
                          {q.name}
                        </h2>
                        <span
                          data-testid={`inquiry-status-${q.id}`}
                          className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${badgeClass(q.status)}`}
                        >
                          {q.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-400">
                        {q.email} {q.phone ? `\u25C8 ${q.phone}` : ""}
                      </p>
                      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                        {q.event_type}
                        {q.event_date ? ` \u25C8 ${q.event_date}` : ""}
                        {q.venue ? ` \u25C8 ${q.venue}` : ""}
                        {q.guests ? ` \u25C8 ${q.guests} guests` : ""}
                        {q.budget ? ` \u25C8 ${q.budget}` : ""}
                      </p>
                      {q.message && (
                        <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-3xl">
                          {q.message}
                        </p>
                      )}
                      <p className="mt-3 text-[11px] text-slate-600 font-mono">
                        Received {new Date(q.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex lg:flex-col items-center lg:items-end gap-3 shrink-0">
                      <label
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"
                        htmlFor={`status-${q.id}`}
                      >
                        Status
                      </label>
                      <select
                        id={`status-${q.id}`}
                        data-testid={`inquiry-status-select-${q.id}`}
                        value={q.status}
                        onChange={(e) => setStatus(q.id, e.target.value)}
                        className="field !w-auto py-2 text-xs"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="confirmed">Confirmed</option>
                      </select>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
